import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

/**
 * "About Us" — tight identity slate that mirrors the hero video's
 * opening title card. Russ asked (2026-05-24) to remove the prior
 * pillars + long-form prose; the section now reads as a single
 * focused brand-statement above the Offerings grid, leaving the
 * detailed product info to live on the per-page Detail routes
 * (which the offering cards link to) and on the rafting deep-dive
 * <About /> component below this one.
 *
 * Structure:
 *   1. Eyebrow "ABOUT US"
 *   2. Display title  — "Your mountain basecamp on the Ocoee"
 *   3. Italic prose   — short positioning paragraph
 *   4. CTAs           — Book / Stay
 */
export function AboutUs() {
  const root = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const reduce =
        typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (reduce) return;

      // Slate + CTAs fade-up together as one storytelling beat.
      gsap.from(".about-us-slate > *, .about-us-ctas", {
        y: 22,
        opacity: 0,
        duration: 0.8,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 82%" },
      });
    },
    { scope: root },
  );

  return (
    <section ref={root} className="about-us" aria-labelledby="about-us-heading">
      <div className="about-us-inner">
        {/* Slate — strong identity hit before the pillars unfold */}
        <div className="about-us-slate">
          <span className="eye eye-inverse">ABOUT US</span>
          <h2 id="about-us-heading" className="h2 about-us-title">
            Your mountain basecamp
            <br />
            near the Ocoee River
          </h2>
          <p className="about-us-lead">
            A whitewater rafting and outdoor adventure company near the Ocoee
            River and Cherokee National Forest in Benton, Tennessee.
          </p>
        </div>

        <div className="about-us-ctas">
          <Link to="/rafting" className="btn btn-gold btn-sm">
            See Rafting Trips
          </Link>
          <Link to="/lodge" className="btn btn-ghost btn-sm">
            Stay With Us
          </Link>
        </div>
      </div>
    </section>
  );
}
